/**
 * User Management Page
 * Manage and view all users across the platform
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useDebounce } from '../../hooks';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableHead,
  Avatar,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MainLayout } from '../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState, TablePagination } from '../../components/common';
import { Card, CardContent, TableCell, TableHeaderCell, TableRow, TableContainer, SearchBox, Select } from '../../components/ui';
import { UserService } from '../../services/user/UserService';
import type {
  User,
  UserStatus,
  UserType,
  PaginatedUsers,
} from '../../types/user';

type StatusFilter = UserStatus | 'all';
type UserTypeFilter = UserType | 'all';

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'pending', label: 'Pending' },
  { value: 'suspended', label: 'Suspended' },
];

const userTypeFilters: { value: UserTypeFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'asset', label: 'Asset' },
  { value: 'ghost', label: 'Ghost' },
];

const statusLabels: Record<UserStatus, string> = {
  active: 'Active',
  disabled: 'Disabled',
  pending: 'Pending',
  suspended: 'Suspended',
};

const userTypeLabels: Record<UserType, string> = {
  asset: 'Asset',
  ghost: 'Ghost',
};

const getStatusDotColor = (status: UserStatus, theme: any): string => {
  switch (status) {
    case 'active':
      return theme.palette.success.main;
    case 'disabled':
      return theme.palette.error.main;
    case 'pending':
      return theme.palette.warning.main;
    case 'suspended':
      return theme.palette.grey[500];
    default:
      return theme.palette.text.secondary;
  }
};

const UserManagementPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearchValue = useDebounce(searchValue, 500);
  const [activeStatus, setActiveStatus] = useState<StatusFilter>('all');
  const [activeUserType, setActiveUserType] = useState<UserTypeFilter>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalResults, setTotalResults] = useState(0);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      const result: PaginatedUsers = await UserService.getUsers(
        page,
        pageSize,
        {
          status: activeStatus,
          userType: activeUserType,
          search: debouncedSearchValue || undefined,
        }
      );
      setUsers(result.data);
      setTotalResults(result.total);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, activeStatus, activeUserType, debouncedSearchValue]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
    setPage(1);
  };

  const handleStatusChange = (status: StatusFilter) => {
    setActiveStatus(status);
    setPage(1);
  };

  const handleUserTypeChange = (userType: UserTypeFilter) => {
    setActiveUserType(userType);
    setPage(1);
  };

  const handleViewUser = (user: User) => {
    // Navigate to Asset detail page for Asset users, otherwise to general user detail page
    if (user.userType === 'asset') {
      navigate(`/users/asset/${user.id}`);
    } else {
      navigate(`/users/${user.id}`);
    }
  };

  const handlePageSizeChange = (event: SelectChangeEvent<number>) => {
    setPageSize(Number(event.target.value));
    setPage(1);
  };

  const getAvatarInitials = (name: string): string => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name[0].toUpperCase();
  };

  return (
    <MainLayout>
      {/* Page Header */}
      <Box sx={{ marginBottom: theme.spacing(3) }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: theme.typography.h4.fontWeight,
            color: theme.palette.text.primary,
            marginBottom: theme.spacing(0.5),
          }}
        >
          User Management
        </Typography>
        <Typography
          variant="body1"
          sx={{ color: theme.palette.text.secondary }}
        >
          View and manage all users. Asset and Ghost users register themselves.
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
          flexWrap: 'wrap',
          gap: theme.spacing(2),
        }}
      >
        {/* Search Box */}
        <SearchBox
          placeholder="Search by call sign, email..."
          value={searchValue}
          onChange={handleSearch}
          containerSx={{ flex: 1, minWidth: 300 }}
        />

        {/* Status Filter */}
        <Select
          value={activeStatus}
          onChange={(e) => handleStatusChange(e.target.value as StatusFilter)}
          displayEmpty
          size="small"
          renderValue={(value) => {
            const filter = statusFilters.find((f) => f.value === value);
            return `Status: ${filter?.label || 'All'}`;
          }}
          formControlProps={{
            sx: {
              minWidth: 150,
            },
          }}
          sx={{
            color: theme.palette.text.primary,
            '& .MuiSelect-select': {
              padding: theme.spacing(1.5, 2),
            },
          }}
        >
          {statusFilters.map((filter) => (
            <MenuItem key={filter.value} value={filter.value}>
              {filter.label}
            </MenuItem>
          ))}
        </Select>

        {/* User Type Filter */}
        <Select
          value={activeUserType}
          onChange={(e) => handleUserTypeChange(e.target.value as UserTypeFilter)}
          displayEmpty
          size="small"
          renderValue={(value) => {
            const filter = userTypeFilters.find((f) => f.value === value);
            return `User Type: ${filter?.label || 'All'}`;
          }}
          formControlProps={{
            sx: {
              minWidth: 150,
            },
          }}
          sx={{
            color: theme.palette.text.primary,
            '& .MuiSelect-select': {
              padding: theme.spacing(1.5, 2),
            },
          }}
        >
          {userTypeFilters.map((filter) => (
            <MenuItem key={filter.value} value={filter.value}>
              {filter.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Users Table */}
      <Card>
        <CardContent noPadding>
          {isLoading ? (
            <Box sx={{ padding: theme.spacing(6), display: 'flex', justifyContent: 'center' }}>
              <LoadingSpinner />
            </Box>
          ) : users.length === 0 ? (
            <Box sx={{ padding: theme.spacing(4) }}>
              <EmptyState
                title="No Users Found"
                message="There are no users matching your criteria."
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>User</TableHeaderCell>
                      <TableHeaderCell>Status</TableHeaderCell>
                      <TableHeaderCell>User Type</TableHeaderCell>
                      <TableHeaderCell>Last Login</TableHeaderCell>
                      <TableHeaderCell>Actions</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user, index) => (
                      <TableRow key={`${user.id}-${index}`}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1.5) }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                backgroundColor: user.userType === 'asset'
                                  ? theme.palette.primary.main
                                  : theme.palette.secondary.main,
                                fontSize: theme.typography.body2.fontSize,
                              }}
                              src={user.avatar}
                            >
                              {!user.avatar && getAvatarInitials(user.name)}
                            </Avatar>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.primary,
                                  fontWeight: theme.typography.subtitle2.fontWeight,
                                  fontSize: theme.typography.body2.fontSize,
                                }}
                              >
                                {user.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: theme.palette.text.secondary,
                                  fontSize: theme.typography.caption.fontSize,
                                }}
                              >
                                {user.email}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                            <Box
                              sx={{
                                width: theme.spacing(1),
                                height: theme.spacing(1),
                                borderRadius: theme.shapes.borderRadius.round,
                                backgroundColor: getStatusDotColor(user.status, theme),
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: theme.palette.text.primary,
                                fontSize: theme.typography.body2.fontSize,
                              }}
                            >
                              {statusLabels[user.status]}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          {userTypeLabels[user.userType]}
                        </TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>
                          {user.lastLoginText || 'Never'}
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            onClick={() => handleViewUser(user)}
                            sx={{
                              color: theme.palette.primary.main,
                              cursor: 'pointer',
                              fontWeight: theme.typography.body2.fontWeight,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            View
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Pagination - Moved outside TableContainer to keep it fixed */}
              <TablePagination
                page={page}
                pageSize={pageSize}
                totalItems={totalResults}
                onPageChange={setPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </>
          )}
        </CardContent>
      </Card>
    </MainLayout>
  );
};

export default UserManagementPage;

